import {inject, injectable} from "inversify";
import {action, computed, configure, makeObservable, observable, when} from "mobx";
import {Subscription} from "rxjs";
import {ListPaginationEntity} from "@dropDesk/domain/entities/common/list_pagination.entity";
import {InternalChatEntity} from "@dropDesk/domain/entities/internal_chat/internal_chat_entity";
import {
    BodyMessage,
    InternalChatMessageEntity
} from "@dropDesk/domain/entities/internal_chat/messages/internal_chat_message_entity";
import {InternalChatListFilters} from "@dropDesk/domain/entities/internal_chat/internal_chat_list_filters";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {SubscribeInternalChatsUseCase} from "@dropDesk/domain/use_case/internal_chat/subscribe_internal_chats.usecase";
import {ListInternalChatsUseCase} from "@dropDesk/domain/use_case/internal_chat/list_internal_chats.usecase";
import {
    SendInternalChatMessageUseCase
} from "@dropDesk/domain/use_case/internal_chat/send_internal_chat_message.usecase";
import {
    DeleteInternalChatMessageUseCase
} from "@dropDesk/domain/use_case/internal_chat/delete_internal_chat_message.usecase";
import {
    InformReadInternalChatMessagesUseCase
} from "@dropDesk/domain/use_case/internal_chat/inform_read_internal_chat_messages.usecase";
import {InternalChatRepository} from "@dropDesk/domain/repositories/internal_chat/internal_chat.repository";
import toastMessage from "@dropDesk/utils/toast_message/toast_message";
import {translate} from "@dropDesk/storage/i18n/translate_helper";
import {CancelTokenSource} from "axios";
import {removeDiacritics} from "@dropDesk/utils/helpers/string_helper";
import {
    ClearInternalChatsCacheUseCase
} from "@dropDesk/domain/use_case/internal_chat/clear_internal_chats_cache.usecase";
import {
    UpdateCacheFromSingleInternalChatUseCase
} from "@dropDesk/domain/use_case/internal_chat/update_cache_from_single_internal_chat.usecase";
import {SetInternalChatUseCase} from "@dropDesk/domain/use_case/internal_chat/set_internal_chat.usecase";
import {
    ChangeDescriptionInternalChatUseCase
} from "@dropDesk/domain/use_case/internal_chat/change_description_internal_chat.usecase";
import {DeleteInternalChatUseCase} from "@dropDesk/domain/use_case/internal_chat/delete_internal_chat.usecase";
import {ListInternalChatUsersUseCase} from "@dropDesk/domain/use_case/internal_chat/list_internal_chat_users.usecase";
import {
    ChangeUsersInternalChatUseCase
} from "@dropDesk/domain/use_case/internal_chat/change_users_internal_chat.usecase";
import {
    SubscriptionUploadFileInternalChatUseCase
} from "@dropDesk/domain/use_case/internal_chat/upload_file_internal_chat_message.usecase";
import {
    NavigateToInternalChatMessageUseCase
} from "@dropDesk/domain/use_case/internal_chat/navigate_to_internal_chat_message.usecase";
import {
    ReactInternalChatMessageUseCase
} from "@dropDesk/domain/use_case/internal_chat/react_internal_chat_message.usecase";
import {
    EditInternalChatMessageUseCase
} from "@dropDesk/domain/use_case/internal_chat/edit_internal_chat_message.usecase";
import {ConstantsKeys} from "@dropDesk/domain/entities/constants/constants_keys";
import {Howl} from "howler";
import soundNotificationMessage from "@dropDesk/notification_new_message_chat_internal.mp3";
import {newInternalChat} from "@dropDesk/domain/entities/internal_chat/new_internal_chat";
import {generateUUIDV4} from "@dropDesk/utils/uuidv4/uuidv4";
import {InternalChatUserEntity} from "@dropDesk/domain/entities/internal_chat/users/internal_chat_user_entity";
import {BackendAction} from "@dropDesk/domain/entities/common/actions_entity";
import {
    findMaxNumber,
    findMinNumber,
    getIdCompanyByLocalStorage,
    getUniqueListById,
    sleep
} from "@dropDesk/utils/helpers/common";
import {InternalChatUsersListFilters} from "@dropDesk/domain/entities/internal_chat/internal_chat_users_list_filters";
import {CancelTokenManager} from "@dropDesk/data/clients/http.client";

import {getFormattedDate, getNowSumIndex} from "@dropDesk/utils/helpers/date_helper";
import {FileMessageEntity} from "@dropDesk/domain/entities/common/file_message";
import {
    AudioMessageEntity,
    convertFileToMessageEntity,
    getMessageTypeFromFile,
    MessageChatType, MessageStatus, ReactionMessageEntity, ReplyEntity
} from "@dropDesk/domain/entities/ticket/message/external/message_entity";
import {UploadOptionsEntity} from "@dropDesk/domain/entities/ticket/message/upload_message.entity";
import firebase from "firebase/compat";
import FirebaseStorageError = firebase.storage.FirebaseStorageError;
import {BaseMessageEntity} from "@dropDesk/domain/entities/messages/base_message_entity";
import {newInternalChatMessage} from "@dropDesk/presentation/pages/chat/controller/new_internal_chat_message";
import {AudioRecordEntity} from "@dropDesk/domain/entities/common/audio_record.entity";
import {fileToBase64} from "@dropDesk/utils/helpers/files";
import {UrlToBlobUseCase} from "@dropDesk/domain/use_case/io/url_to_blob.usecase";
import {GetUsersFromCacheUseCase} from "@dropDesk/domain/use_case/internal_chat/get_users_from_cache.usecase";
import {GetMessageByIdUseCase} from "@dropDesk/domain/use_case/internal_chat/get_message_by_id.usecase";
import {ChatMessageEntity} from "@dropDesk/domain/entities/messages/message_enums";
import { DropDeskErrorEnum } from "@dropDesk/domain/entities/exceptions/dropdesk_error_enum";

configure({
    enforceActions: "always",
});

@injectable()
export class InternalChatController {

    private readonly _subscribeInternalChatsUseCase: SubscribeInternalChatsUseCase;
    private readonly _listInternalChatsUseCase: ListInternalChatsUseCase;
    private readonly _repository: InternalChatRepository;
    private readonly _sendInternalChatMessageUseCase: SendInternalChatMessageUseCase;
    private readonly _deleteInternalChatMessageUseCase: DeleteInternalChatMessageUseCase;
    private readonly _informReadInternalChatMessagesUseCase: InformReadInternalChatMessagesUseCase;
    private readonly _clearInternalChatsCacheUseCase: ClearInternalChatsCacheUseCase;
    private readonly _updateCacheFromSingleInternalChatUseCase: UpdateCacheFromSingleInternalChatUseCase;
    private readonly _setInternalChatUseCase: SetInternalChatUseCase;
    private readonly _changeDescriptionInternalChatUseCase: ChangeDescriptionInternalChatUseCase;
    private readonly _deleteInternalChatUseCase: DeleteInternalChatUseCase;
    private readonly _listInternalChatUsersUseCase: ListInternalChatUsersUseCase;
    private readonly _changeUsersInternalChatUseCase: ChangeUsersInternalChatUseCase;
    private readonly _subscriptionUploadFileUseCase: SubscriptionUploadFileInternalChatUseCase;
    private readonly _navigateToMessageUseCase: NavigateToInternalChatMessageUseCase;
    private readonly _reactionMessageUseCase: ReactInternalChatMessageUseCase;
    private readonly _urlToBlobUseCase: UrlToBlobUseCase;
    private readonly _editMessageUseCase: EditInternalChatMessageUseCase;
    private readonly _getUsersFromCacheUseCase: GetUsersFromCacheUseCase;
    private readonly _getMessageByIdUseCase: GetMessageByIdUseCase;

    constructor(
        @inject(SubscribeInternalChatsUseCase) subscribeInternalChatsUseCase: SubscribeInternalChatsUseCase,
        @inject(ListInternalChatsUseCase) listInternalChatsUseCase: ListInternalChatsUseCase,
        @inject(InternalChatRepository) repository: InternalChatRepository,
        @inject(SendInternalChatMessageUseCase) sendInternalChatMessageUseCase: SendInternalChatMessageUseCase,
        @inject(DeleteInternalChatMessageUseCase) deleteInternalChatMessageUseCase: DeleteInternalChatMessageUseCase,
        @inject(InformReadInternalChatMessagesUseCase) informReadInternalChatMessagesUseCase: InformReadInternalChatMessagesUseCase,
        @inject(ClearInternalChatsCacheUseCase) clearInternalChatsCacheUseCase: ClearInternalChatsCacheUseCase,
        @inject(UpdateCacheFromSingleInternalChatUseCase) updateCacheFromSingleInternalChatUseCase: UpdateCacheFromSingleInternalChatUseCase,
        @inject(SetInternalChatUseCase) setInternalChatUseCase: SetInternalChatUseCase,
        @inject(ChangeDescriptionInternalChatUseCase) changeDescriptionInternalChatUseCase: ChangeDescriptionInternalChatUseCase,
        @inject(DeleteInternalChatUseCase) deleteInternalChatUseCase: DeleteInternalChatUseCase,
        @inject(ListInternalChatUsersUseCase) listInternalChatUsersUseCase: ListInternalChatUsersUseCase,
        @inject(ChangeUsersInternalChatUseCase) changeUsersInternalChatUseCase: ChangeUsersInternalChatUseCase,
        @inject(SubscriptionUploadFileInternalChatUseCase) subscriptionUploadFileUseCase: SubscriptionUploadFileInternalChatUseCase,
        @inject(NavigateToInternalChatMessageUseCase) navigateToMessageUseCase: NavigateToInternalChatMessageUseCase,
        @inject(ReactInternalChatMessageUseCase) reactionMessageUseCase: ReactInternalChatMessageUseCase,
        @inject(UrlToBlobUseCase) urlToBlobUseCase: UrlToBlobUseCase,
        @inject(EditInternalChatMessageUseCase) editMessageUseCase: EditInternalChatMessageUseCase,
        @inject(GetUsersFromCacheUseCase) getUsersFromCacheUseCase: GetUsersFromCacheUseCase,
        @inject(GetMessageByIdUseCase) getMessageByIdUseCase: GetMessageByIdUseCase,
    ) {
        makeObservable(this);
        this._subscribeInternalChatsUseCase = subscribeInternalChatsUseCase;
        this._listInternalChatsUseCase = listInternalChatsUseCase;
        this._repository = repository;
        this._sendInternalChatMessageUseCase = sendInternalChatMessageUseCase;
        this._deleteInternalChatMessageUseCase = deleteInternalChatMessageUseCase;
        this._informReadInternalChatMessagesUseCase = informReadInternalChatMessagesUseCase;
        this._clearInternalChatsCacheUseCase = clearInternalChatsCacheUseCase;
        this._updateCacheFromSingleInternalChatUseCase = updateCacheFromSingleInternalChatUseCase;
        this._setInternalChatUseCase = setInternalChatUseCase;
        this._changeDescriptionInternalChatUseCase = changeDescriptionInternalChatUseCase;
        this._deleteInternalChatUseCase = deleteInternalChatUseCase;
        this._listInternalChatUsersUseCase = listInternalChatUsersUseCase;
        this._changeUsersInternalChatUseCase = changeUsersInternalChatUseCase;
        this._subscriptionUploadFileUseCase = subscriptionUploadFileUseCase;
        this._navigateToMessageUseCase = navigateToMessageUseCase;
        this._reactionMessageUseCase = reactionMessageUseCase;
        this._urlToBlobUseCase = urlToBlobUseCase;
        this._editMessageUseCase = editMessageUseCase;
        this._getUsersFromCacheUseCase = getUsersFromCacheUseCase;
        this._getMessageByIdUseCase = getMessageByIdUseCase;
    }

    @observable
    tableListInternalChats: ListPaginationEntity<InternalChatEntity> = ListPaginationEntity.defaultValue<InternalChatEntity>();

    @observable
    tableInternalChatUsers: ListPaginationEntity<InternalChatUserEntity> = ListPaginationEntity.defaultValue<InternalChatUserEntity>();

    @observable
    currentInternalChat?: InternalChatEntity | null;

    @observable
    currentInternalChatMessages: ListPaginationEntity<InternalChatMessageEntity> = ListPaginationEntity.defaultValue<InternalChatMessageEntity>();

    @observable
    soundNewMessageInternalChat: boolean = localStorage.getItem(ConstantsKeys.soundNewMessageInternalChat)
        ?
        JSON.parse(localStorage.getItem(ConstantsKeys.soundNewMessageInternalChat)!)
        :
        true;

    @observable
    notificationNewMessage: Howl | null = null;

    @observable
    subscriptionInternalChats?: Subscription;

    @observable
    cacheChat: Record<string, Record<number, InternalChatMessageEntity[]>> = {};

    @observable
    searchParam = '';

    @observable
    searchParamUsers = '';

    @observable
    loading = false;

    @observable
    loadingListUsers = false;

    @observable
    loadingMessage?: string | null = null;

    @observable
    internalChat?: InternalChatEntity;

    @observable
    internalChatUsersDefault: InternalChatUserEntity[] = [];

    @observable
    internalChatNotFound: boolean = false;

    @observable
    subscriptionUploadFile?: Subscription;

    @observable
    forceCleanMessagesCache: boolean = false;

    @observable
    targetVirtualizedMessage: InternalChatMessageEntity | null = null;

    @observable
    scrollPosition: 'top' | 'bottom' | null = null;

    @observable
    idMessageFocused: string | null = null;

    @observable
    lastUsedReactions: string[] =
        (
            (localStorage.getItem(ConstantsKeys.lastUsedReactions as string) as string[] | null)
                ?
                JSON.parse(localStorage.getItem(ConstantsKeys.lastUsedReactions as string)!)
                :
                ['👍', '❤️', '😂', '😮', '😢', '🙏']
        );

    @observable
    readByUsers: UserEntity[] = [];

    @observable
    receivedByUsers: UserEntity[] = [];

    user?: UserEntity;

    @computed
    get countNotificationDocumentHead(): number {
        return (
            (this.tableListInternalChats.data.filter((entry) => !!entry.countUnreadNotification)).length
        )
    }

    @computed
    get filteredList(): InternalChatEntity[] {
        if (this.searchParam !== '') {
            return this.tableListInternalChats.data.filter((internalChat) => this.filterPredicate(internalChat));
        }
        return this.tableListInternalChats.data;
    }

    @computed
    get messages(): InternalChatMessageEntity[] {
        if (this.forceCleanMessagesCache) {
            return [];
        }
        return this.orderMessagesByCreatedAt(
            this.removeDuplicateMessages(
                this.currentInternalChatMessages.data.filter((entry) => !this.currentInternalChat?.hasActiveStatus ? !entry.reactionMessage && !entry.isSystemMessage : !entry.reactionMessage))
        );
    }

    @computed
    get reactions(): InternalChatMessageEntity[] {
        return this.removeDuplicateMessages(
            this.currentInternalChatMessages.data.filter((entry) => !!entry.reactionMessage)
        );

    }

    @computed
    get targetMessagePaginationTop(): InternalChatMessageEntity | undefined {
        if (
            this.forceCleanMessagesCache
            ||
            this.loading
            ||
            this.targetVirtualizedMessage
        ) {
            return;
        }
        return this.messages[this.messages.length - 10];
    }

    @computed
    get targetMessageButtonNavigateBottom(): InternalChatMessageEntity | undefined {
        if (this.forceCleanMessagesCache || this.messages.length === 0) {
            return;
        }
        return this.messages[0];
    }


    @computed
    get targetMessagePaginationBottom(): InternalChatMessageEntity | undefined {
        if (this.forceCleanMessagesCache || this.loading || this.targetVirtualizedMessage) {
            return;
        }

        if (this.currentInternalChatMessages.hasMoreBottom) {
            return this.messages[this.messages.length > 10 ? 10 : this.messages.length - 1];
        }
        return;
    }


    @action
    removeDuplicateMessages(messages: InternalChatMessageEntity[]): InternalChatMessageEntity[] {
        return BaseMessageEntity.removeDuplicateMessages(messages);
    }

    orderMessagesByCreatedAt(messages: InternalChatMessageEntity[]) {
        return BaseMessageEntity.orderMessagesByCreatedAt(messages);
    }

    @action
    setForceCleanMessagesCache(value: boolean) {
        this.forceCleanMessagesCache = value;
    }

    @action
    setIdMessageFocused(id: string | null) {
        this.idMessageFocused = id;
    }

    @action
    handleResetMessageFocused = () => {
        this.setIdMessageFocused(null);
    }

    @action
    setInternalChatNotFound(value: boolean) {
        this.internalChatNotFound = value;
    }

    @action
    setTableListInternalChats(value: ListPaginationEntity<InternalChatEntity>) {
        this.tableListInternalChats = value;
    }

    @action
    setTableListInternalChatsUsers(value: ListPaginationEntity<InternalChatUserEntity>) {
        this.tableInternalChatUsers = value;
    }

    @action
    pushUserLoadMoreData(value: ListPaginationEntity<InternalChatUserEntity>) {
        const data = getUniqueListById(this.tableInternalChatUsers.data, value.data, 'idUser').map(entry => InternalChatUserEntity.fromJson(entry));
        this.setTableListInternalChatsUsers(this.tableInternalChatUsers.copyWith({
            ...value,
            data
        }));
    }

    @action
    setCurrentInternalChat(internalChat: InternalChatEntity, eraseMessages = false) {
        const internalChatFromJson = InternalChatEntity.fromJson(internalChat as Record<string, any>);
        const eligibleSet = internalChatFromJson.hasEligibleInternalChat(this.user!);
        if (eligibleSet) {
            this.currentInternalChat = this.currentInternalChat && this.currentInternalChat.id === internalChat.id ? internalChat.preventDeleteRelations(this.currentInternalChat) : internalChatFromJson;
        }
        if (eraseMessages) {
            this.eraseMessages();
        }
    }

    @action
    eraseMessages(): void {
        const data = this.getMessagesCacheFromPage(0);
        const initialListPagination = new ListPaginationEntity<InternalChatMessageEntity>({
            pages: 0,
            page: 0,
            limit: ConstantsKeys.defaultLimit,
            totalRows: 0,
            data,
            hasMore: false,
            hasMoreBottom: false
        });

        if (data && data.length) {
            this.updateMessage(this.currentInternalChat!, data, false, true, initialListPagination, false, true);
        } else {
            this.setCurrentInternalChatMessages(initialListPagination);
        }
        this.handleVirtualizedMessages();
    }

    @action
    setTargetVirtualizedMessage(message: InternalChatMessageEntity | null) {
        this.targetVirtualizedMessage = message;
    }

    @action
    handleVirtualizedMessages() {
        const count = Math.floor((window.innerHeight - 302) / 60) + 5;
        if (this.messages.length > count) {
            this.setTargetVirtualizedMessage(this.messages[count]);
        } else {
            this.setTargetVirtualizedMessage(null);
        }
    }

    @action
    getMessagesCacheFromPage(page: number): InternalChatMessageEntity[] {
        const cache = this.cacheChat[this.currentInternalChat?.id ?? 'unknown'] ?? {};
        const messages = cache[page] ?? [];
        return messages.map(message => InternalChatMessageEntity.fromJson(message));
    }

    @action
    setInternalChat(internalChat: InternalChatEntity) {
        this.internalChat = internalChat;
    }

    @action
    setInternalChatUsersDefault(internalChatUsers: InternalChatUserEntity[]) {
        this.internalChatUsersDefault = internalChatUsers;
    }

    @action
    setCurrentInternalChatMessages(value: ListPaginationEntity<InternalChatMessageEntity>) {
        this.currentInternalChatMessages = value;
    }

    @action
    setSearchParam(value: string) {
        this.searchParam = value;
    }

    @action
    setSearchParamUsers(value: string) {
        this.searchParamUsers = value;
    }

    @action
    handleScrollTopVirtualizedMessage() {
        this.setTargetVirtualizedMessage(null);
    }

    @action
    setSoundNewMessageInternalChat(value: boolean) {
        this.soundNewMessageInternalChat = value;
        localStorage.setItem(ConstantsKeys.soundNewMessageInternalChat, value.toString());
    }

    @action
    filterPredicate(internalChat: InternalChatEntity): boolean {
        const searchParam = removeDiacritics(this.searchParam).toLowerCase();

        const userMatch = internalChat.users?.some((user: any) =>
            removeDiacritics(user.user?.name?.toLowerCase() ?? '').includes(searchParam)
        ) ?? false;

        const descriptionMatch = removeDiacritics(internalChat.description?.toLowerCase() ?? '').includes(searchParam);

        return userMatch || descriptionMatch;
    }

    @action
    resetTable() {
        this.setTableListInternalChats(ListPaginationEntity.defaultValue<InternalChatEntity>());
    }

    @action
    resetTableUsers() {
        this.setTableListInternalChatsUsers(ListPaginationEntity.defaultValue<InternalChatUserEntity>());
    }

    orderInternalChatsByUpdatedAt(internalChats: InternalChatEntity[]) {
        internalChats.sort((a, b) => new Date(b.updatedAt!).getTime() - new Date(a.updatedAt!).getTime());
    }

    private getUniqueList(originalList: InternalChatEntity[], newList: InternalChatEntity[]): InternalChatEntity[] {
        const result = originalList;

        if (newList.length) {
            for (const internalChat of newList) {
                const index = originalList.findIndex((item) => item.id === internalChat.id);
                if (index > -1) {
                    result[index] = internalChat;
                } else {
                    result.push(internalChat);
                }
            }
        }

        return result;
    }

    @action
    makeInternalChat = (internalChat?: InternalChatEntity): void => {
        this.setInternalChat(internalChat ?? newInternalChat(this.user!));
    }

    @action
    pushInternalChatData(value: ListPaginationEntity<InternalChatEntity>) {
        const eligibleList = value.data.filter((internalChat) => internalChat.hasEligibleInternalChat(this.user!));
        this.removeUnEligibleTable(eligibleList, this.tableListInternalChats);
        const data = this.getUniqueList(this.tableListInternalChats.data, eligibleList);
        this.orderInternalChatsByUpdatedAt(data);
        this.setTableListInternalChats(this.tableListInternalChats.copyWith({
            ...value,
            data,
        }));
    }

    @action
    eraseInternalChat() {
        this.currentInternalChat = null;
        this.setCurrentInternalChatMessages(ListPaginationEntity.defaultValue<InternalChatMessageEntity>());
    }

    @action
    private notificationAudioNewMessage(internalChats: InternalChatEntity[]) {
        for (const internalChat of internalChats) {
            if (
                this.soundNewMessageInternalChat &&
                internalChat.soundPlayed
            ) {
                this.notificationNewMessage?.play();
                internalChat.soundPlayed = false;
                this.updateCacheDataSource(internalChat);
            }
        }
    }

    @action
    updateCacheDataSource(internalChat: InternalChatEntity): void {
        this._updateCacheFromSingleInternalChatUseCase.call(internalChat);
    }

    @action
    setScrollPosition(value: 'top' | 'bottom' | null) {
        this.scrollPosition = value;
    }

    @action
    resetSounds(): void {
        this.notificationNewMessage = null;
    }

    @action
    startLoading(message?: string) {
        this.loading = true;
        this.loadingMessage = message ?? null;
    }

    @action
    stopLoading() {
        this.loading = false;
        this.loadingMessage = null;
    }

    @action
    startLoadingUsers() {
        this.loadingListUsers = true;
    }

    @action
    stopLoadingUsers() {
        this.loadingListUsers = false;
    }

    @action
    initialize = async (
        userLogged: UserEntity,
        cancelTokenSource?: CancelTokenSource
    ): Promise<void> => {
        try {
            this.user = userLogged;
            await this.listInternalChats('', cancelTokenSource);
            this.notificationNewMessage = new Howl({
                src: [soundNotificationMessage],
                volume: 0.6,
            });
        } catch (error) {
            console.log('error initialize internal chat', error);
        }
    }

    @action
    updateCacheMessagesFromSubs(internalChats: InternalChatEntity[]): void {
        const keys = Object.keys(this.cacheChat);

        const toRemove: string[] = keys.filter(key =>
            !internalChats.some(ticket => ticket.id === key)
        );

        for (const key of toRemove) {
            delete this.cacheChat[key];
        }

        for (const ticket of internalChats) {
            if (ticket.lastMessage) {
                this.setCacheChat(ticket, [ticket.lastMessage!], 0);
            }
        }
    }

    @action
    private setChatToActiveChatFromSubs(data: InternalChatEntity[]): void {
        const target: InternalChatEntity | null = this.getTargetInternalChat(data);
        if (target) {
            this.setCurrentInternalChat(target, false);
        }
    }

    private getTargetInternalChat(internalChats: InternalChatEntity[]): InternalChatEntity | null {
        return internalChats.find((internalChat: InternalChatEntity) => internalChat.id === this.currentInternalChat?.id) ?? null;
    }

    @action
    private setMessagesToActiveChat(data: InternalChatEntity[]): void {
        const target: InternalChatEntity | null = this.getTargetInternalChat(data);
        if (target) {
            if (target.lastMessage) {
                this.updateMessage(target, [target.lastMessage], true, true);
            }
            if (target?.messages?.length) {
                this.updateMessage(target, target.messages, true, true);
            }
        }
    }

    @action
    observerInternalChats = (): void => {
        this.subscriptionInternalChats?.unsubscribe();
        this.subscriptionInternalChats = this._subscribeInternalChatsUseCase.call().subscribe({
            next: (internalChats) => {
                this.updateCacheMessagesFromSubs(internalChats);
                this.setChatToActiveChatFromSubs(internalChats);
                this.setMessagesToActiveChat(internalChats);
                this.handleChangeList(internalChats);
                this.informReadMessagesIfNeeded(this.getTargetInternalChat(internalChats)?.messages).then();
            },
            error: (error) => {
                toastMessage.error(translate(`server_messages.${error.message ?? 'unknown'}`));
                this.stopLoading();
            }
        });
    }

    @action
    listInternalChats = async (searchParam: string, cancelTokenSource?: CancelTokenSource): Promise<void> => {
        try {
            this.startLoading();
            if (searchParam !== this.searchParam) {
                this.resetTable();
                this.eraseInternalChat();
            }
            this.setSearchParam(searchParam ?? '');
            const filters = new InternalChatListFilters({
                searchParam,
                page: this.tableListInternalChats.page,
                limit: this.tableListInternalChats.limit,
                listOnlyDeleted: false,
                cancelTokenSource
            });
            const response = await this._listInternalChatsUseCase.call(filters);
            this.setTableListInternalChats(response);
            this.stopLoading();
        } catch (err: any) {
            if (err.message !== DropDeskErrorEnum.operationCanceledDueToNewRequest) {
                toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            }
            this.stopLoading();
        }
    }

    @action
    getDataFromPageInternalChats = async (): Promise<void> => {
        if (this.tableListInternalChats.hasMore) {
            this.startLoading();
            const newPage = this.tableListInternalChats.page + 1;
            const filters = new InternalChatListFilters({
                searchParam: this.searchParam,
                page: newPage,
                limit: this.tableListInternalChats.limit,
                listOnlyDeleted: false
            });
            const response = await this._listInternalChatsUseCase.call(filters);
            this.pushInternalChatData(response);
            this.stopLoading();
        }
    }

    @action
    dispose(): void {
        this.subscriptionInternalChats?.unsubscribe();
        this.subscriptionUploadFile?.unsubscribe();
    }

    @action
    disposeListUsers(): void {
        this.resetTableUsers();
    }


    @action
    removeAllValues(): void {
        this.cacheChat = {};
        this._clearInternalChatsCacheUseCase.call();
        this.setCurrentInternalChatMessages(ListPaginationEntity.defaultValue<InternalChatMessageEntity>());
        this.setTableListInternalChats(ListPaginationEntity.defaultValue<InternalChatEntity>());
        this.eraseInternalChat();
        this.resetSounds();
    }

    handleChangeList(data: InternalChatEntity[]): void {
        const list = data.filter((item) => item.hasEligibleInternalChat(this.user!));
        this.pushInternalChatData(this.tableListInternalChats.copyWith({
            data: list,
            totalRows: list.length
        }));
        this.notificationAudioNewMessage(data);
    }

    @action
    handleUpdateInternalChat(internalChat: InternalChatEntity) {
        const data = [...this.tableListInternalChats.data];
        const index = data.findIndex((item) => item.id === internalChat.id);
        if (index > -1) {
            data[index] = internalChat;
        } else {
            data.push(internalChat);
        }
        this.handleChangeList(data);
        this.setCurrentInternalChat(internalChat);
        this.updateCacheDataSource(internalChat);
    }

    @action
    set = async (onSuccess: (key: string) => void): Promise<void> => {
        try {
            this.startLoading("internalChat.loadingSave");
            if (this.internalChat?.isUnsaved()) {
                this.setInternalChat(this.internalChat?.copyWith({
                    id: generateUUIDV4()
                }))
            }
            const internalChat = await this._setInternalChatUseCase.call(this.internalChat!);
            this.handleUpdateInternalChat(internalChat);
            onSuccess("internalChat.success.save");
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    onDeleteUsersLinked = (id: string): void => {
        const index = this.internalChat!.users!.findIndex((entry) => id === entry.idUser && entry.action !== BackendAction.delete);
        if (index > -1) {
            const _users = this.internalChat!.users!;
            if (_users[index].idUser === this.internalChat?.idOpenedByUser) {
                toastMessage.error('Não é permitido remover o atendente que abriu o chat interno.');
                return;
            }
            if (_users[index].action === BackendAction.insert) {
                _users.splice(index, 1);
                this.setInternalChat(this.internalChat!.copyWith({
                    users: _users
                }))
            } else {
                _users[index] = _users[index].copyWith({action: BackendAction.delete, replaceActionIfNull: true});
                this.setInternalChat(this.internalChat!.copyWith({
                    users: _users
                }));
            }
        }
    }

    @action
    onChangeUsersLinked = (user: UserEntity): void => {
        if (user) {
            const index = this.internalChat!.users!.findIndex((entry) => user.id === entry.idUser);
            if (index > -1) {
                const _users = this.internalChat!.users!;
                _users[index] = _users[index].copyWith({action: BackendAction.update, replaceActionIfNull: true});
                this.setInternalChat(this.internalChat!.copyWith({
                    users: _users
                }));
            } else {
                this.onInsertUserLinked(user);
            }
        }
    }

    @action
    onInsertUserLinked = (user: UserEntity): void => {
        this.setInternalChat(this.internalChat!.copyWith({
            users: [...this.internalChat!.users!, new InternalChatUserEntity({
                idCompany: this.user!.idCompany,
                idInternalChat: this.internalChat!.id,
                idUser: user.id,
                user,
                internalChat: this.internalChat,
                action: BackendAction.insert,
            })]
        }));
    }

    @action
    changeDescription = async (onSuccess: (key: string) => void): Promise<void> => {
        const currentInternalChat = InternalChatEntity.fromJson(this.currentInternalChat!);
        try {
            this.handleUpdateInternalChat(this.internalChat!);
            await this._changeDescriptionInternalChatUseCase.call(this.internalChat!.id, this.internalChat!.description);
            onSuccess("internalChat.success.changeDescription");
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.handleUpdateInternalChat(currentInternalChat);
        }
    }

    @action
    delete = async (onSuccess: (key: string) => void): Promise<void> => {
        const currentInternalChat = InternalChatEntity.fromJson(this.currentInternalChat!);
        try {
            this.handleUpdateInternalChat(this.currentInternalChat!.copyWith({
                deletedAt: new Date().toISOString()
            }));
            await this._deleteInternalChatUseCase.call(currentInternalChat.id);
            onSuccess("internalChat.success.delete");
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.handleUpdateInternalChat(currentInternalChat);
        }
    }

    @action
    changeUsers = async (): Promise<void> => {
        try {
            this.startLoading("internalChat.loadingChangeUsers");
            const response = await this._changeUsersInternalChatUseCase.call(
                this.internalChat!.id,
                this.internalChat!.users ?? []
            );
            this.handleUpdateInternalChat(response);
            toastMessage.success(translate("internalChat.success.changeUsers"));
            await this.listInternalChatUsers(this.searchParamUsers);
            this.stopLoading();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    listInternalChatUsers = async (searchParam: string): Promise<void> => {
        try {
            this.startLoadingUsers();
            const cancelTokenSource = CancelTokenManager.createNewToken();
            if (searchParam !== this.searchParamUsers) {
                this.resetTableUsers();
            }
            this.setSearchParamUsers(searchParam ?? '');
            const filters = new InternalChatUsersListFilters({
                page: this.tableInternalChatUsers.page,
                searchParam: this.searchParamUsers,
                limit: this.tableInternalChatUsers.limit,
                idInternalChat: this.currentInternalChat!.id,
                cancelTokenSource
            });
            const response = await this._listInternalChatUsersUseCase.call(filters);
            this.setTableListInternalChatsUsers(response);
            this.stopLoadingUsers();
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoadingUsers();
        } finally {
            CancelTokenManager.clearToken();
        }
    };

    @action
    loadMoreUsersDataFromPage = async (): Promise<void> => {
        try {
            if (this.tableInternalChatUsers.hasMore) {
                const cancelTokenSource = CancelTokenManager.createNewToken();
                this.startLoadingUsers();
                const newPage = this.tableInternalChatUsers.page + 1;
                const filters = new InternalChatUsersListFilters({
                    page: newPage,
                    searchParam: this.searchParamUsers,
                    limit: this.tableInternalChatUsers.limit,
                    idInternalChat: this.currentInternalChat!.id,
                    cancelTokenSource
                });
                const response = await this._listInternalChatUsersUseCase.call(filters);
                this.pushUserLoadMoreData(response);
                this.stopLoadingUsers();
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoadingUsers();
        } finally {
            CancelTokenManager.clearToken();
        }
    }

    @action
    removeUnEligibleTable(tickets: InternalChatEntity[], table: ListPaginationEntity<InternalChatEntity>): void {
        const tableData = table.data;
        const toRemove: string[] = tableData.filter(item =>
            !tickets.some(ticket => ticket.id === item.id)
        ).map(item => item.id);

        for (let i = tableData.length - 1; i >= 0; i--) {
            const item = tableData[i];
            const index = toRemove.findIndex((entry) => entry == item.id);
            if (index > -1) {
                if (this.currentInternalChat?.id === item.id) {
                    this.eraseInternalChat();
                }
                tableData.splice(i, 1);
            }
        }
    }

    @action
    eligibleMessagesToRead(): InternalChatMessageEntity[] {
        if (!this.currentInternalChat) {
            return [];
        }
        return this.currentInternalChatMessages.data.filter((entry) => entry.idInternalChat === this.currentInternalChat!.id);
    }

    @action
    setCacheChat(ticket: InternalChatEntity, messages: InternalChatMessageEntity[], page: number, isActionDelete = false): void {
        const cache = this.cacheChat[ticket.id] ?? {};
        const messagesFromPage = cache[page] ?? [];
        cache[page] = this.getUniqueCacheMessagesList(messagesFromPage, messages, isActionDelete);
        this.cacheChat[ticket.id] = cache;
    }

    @action
    addPagesLoad(value: number) {
        const pagesLoad = this.currentInternalChatMessages.pagesLoad;
        return pagesLoad?.includes(value) ? pagesLoad : [...pagesLoad ?? [], value];
    }

    @action
    private handleSetCurrentChatMessages(
        newMessages: InternalChatMessageEntity[],
        unshiftMessage: boolean = false,
        sortMessages: boolean = true,
        newPaginationMessages?: ListPaginationEntity<InternalChatMessageEntity>,
        isActionDelete = false,
        isReplaceMessage = false
    ): void {

        const messages: InternalChatMessageEntity[] = this.currentInternalChatMessages.data;
        for (const message of newMessages) {
            const index = messages.findIndex((element) => element.id === message.id);
            if (index > -1) {
                if (isActionDelete) {
                    messages.splice(index, 1);
                } else {
                    messages[index] = messages[index].getMessagePreventDefaultCache(message);
                }
            } else {
                if (unshiftMessage) {
                    messages.unshift(message);
                } else {
                    messages.push(message);
                }
            }
        }

        if (sortMessages) {
            messages.sort((a, b) => (new Date(b.realCreatedAt).getTime() - (new Date(a.realCreatedAt).getTime())));
        }

        const currentPage = this.currentInternalChatMessages.page;
        const newPage = newPaginationMessages?.page;
        const page = newPage ?? currentPage;
        const hasMoreBottom = !!(isReplaceMessage && (newPage && newPage > 0));
        this.setCurrentInternalChatMessages(this.currentInternalChatMessages.copyWith({
            data: isReplaceMessage ? newMessages : messages,
            page: newPaginationMessages?.page ?? this.currentInternalChatMessages.page,
            pages: newPaginationMessages?.pages ?? this.currentInternalChatMessages.pages,
            totalRows: newPaginationMessages?.totalRows ?? this.currentInternalChatMessages.totalRows,
            limit: newPaginationMessages?.limit ?? this.currentInternalChatMessages.limit,
            hasMore: newPaginationMessages?.hasMore ?? this.currentInternalChatMessages.hasMore,
            hasMoreBottom: hasMoreBottom ? true : newPage === 0 ? false : this.currentInternalChatMessages.hasMoreBottom,
            pagesLoad: isReplaceMessage ? [page] : this.addPagesLoad(page)
        }));
    }

    @action
    updateMessage(
        internalChat: InternalChatEntity,
        messages: InternalChatMessageEntity[],
        unshiftMessage = false,
        sortMessages = false,
        newPaginationMessages: ListPaginationEntity<InternalChatMessageEntity> | undefined = undefined,
        isActionDelete = false,
        isReplaceMessage = false,
        isNewUpdateAt = false
    ) {
        let updatedMessages: InternalChatMessageEntity[] | null = null;
        if (isNewUpdateAt) {
            const updatedAt = getFormattedDate();
            updatedMessages = messages.map(message => InternalChatMessageEntity.fromJson({
                ...message,
                updatedAt
            }));
        }
        const realMessages = updatedMessages ?? messages;
        const page = newPaginationMessages?.page ?? 0;
        this.setCacheChat(internalChat, realMessages, page, isActionDelete);
        this.handleSetCurrentChatMessages(realMessages, unshiftMessage, sortMessages, newPaginationMessages, isActionDelete, isReplaceMessage);
    }

    @action
    async loadStaticMessages(cancelTokenSource?: CancelTokenSource): Promise<void> {
        try {
            const currentInternalChat = this.currentInternalChat;
            if (currentInternalChat) {
                this.startLoading();
                const response = await this._repository.loadMessages(
                    this.currentInternalChat!.id,
                    this.currentInternalChatMessages.page,
                    cancelTokenSource
                );
                this.updateMessage(currentInternalChat, response.data, true, true, response);
                this.stopLoading();
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    async informReadMessagesIfNeeded(messages?: InternalChatMessageEntity[], cancelTokenSource?: CancelTokenSource): Promise<void> {
        if (messages && messages.length > 0) {
            const isNecessaryReadMessages =
                messages.filter((entry) => entry.senderId !== this.user!.id).length > 0
                && messages.filter((entry) => !!entry.readers && !entry.readers[this.user!.id]).length > 0;
            if (isNecessaryReadMessages) {
                await this.informReadMessages(cancelTokenSource);
            }
        }
    }

    @action
    async informReadMessages(cancelTokenSource?: CancelTokenSource): Promise<void> {
        try {
            const currentInternalChat = this.currentInternalChat;
            if (currentInternalChat) {
                this.resetCountNotification(currentInternalChat.id);
                await this._informReadInternalChatMessagesUseCase.call(currentInternalChat.id, cancelTokenSource);
            }
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    @action
    uploadFile = (
        body: BodyMessage,
    ): Promise<InternalChatMessageEntity> => {
        return new Promise<InternalChatMessageEntity>(async (resolve, reject) => {
            let _message = body.message;
            this.subscriptionUploadFile = this._subscriptionUploadFileUseCase.call(_message).subscribe({
                next: (response) => {
                    _message = response;
                    this.updateMessage(body.internalChat, [response], undefined, undefined,
                        undefined, undefined, undefined, true);
                },
                error: (error: FirebaseStorageError) => {
                    return reject(error);
                },
                complete: () => {
                    this.updateMessage(body.internalChat, [_message.removeUploadOptions().getMessagePreventDefaultCache(_message)],
                        undefined, undefined, undefined, undefined, undefined, true);
                    const newBody = new BodyMessage({
                        internalChat: body.internalChat,
                        sender: body.sender,
                        message: _message
                    });
                    return resolve(newBody.message);
                }
            })
        });
    }

    @action
    writeMessage = async (
        body: BodyMessage,
    ): Promise<InternalChatMessageEntity> => {
        return new Promise<InternalChatMessageEntity>(async (resolve, reject) => {
            try {
                let _message = body.message;
                _message = await this._sendInternalChatMessageUseCase.call(_message);
                this.updateMessage(body.internalChat, [_message], true, false);
                return resolve(_message);
            } catch (error: any) {
                return reject(error);
            }
        });
    }

    @action
    async sendMessage(
        body: BodyMessage,
    ): Promise<void> {
        return new Promise<void>(async (resolve, _) => {
            try {
                this.updateMessage(body.internalChat, [body.message], true, false);
                if (body.message.isFileMessage && body.message.isUnsaved()) {
                    body.message = await this.uploadFile(body);
                    const messages = this.cacheChat[body.internalChat.id][0];
                    await when(
                        () => {
                            const index = messages.findIndex(msg => msg.id === body.message.id);
                            return messages.slice(0, index).every(previousMessage =>
                                previousMessage.createdAt || previousMessage.hasSendError
                            );
                        },
                        {timeout: 60000 * 3}
                    ).catch((error) => console.error(`Mensagem não enviada no tempo limite ${error}`));
                }
                await this.writeMessage(body);
                return resolve();
            } catch (error: any) {
                this.handleWriteMessageError(body.internalChat, body.message);
                toastMessage.error(translate(`server_messages.${error?.code ?? error?.message ?? 'unknown'}`));
            }
        });
    }

    handleWriteMessageError(internalChat: InternalChatEntity, message: InternalChatMessageEntity): void {
        const messageError = message.copyWith({
            status: MessageStatus.ERROR
        });
        this.updateMessage(internalChat, [messageError], undefined, undefined,
            undefined, undefined, undefined, true);
    }

    @action
    pushMemoryMessages(messages: InternalChatMessageEntity[], currentInternalChat: InternalChatEntity, userLogged: UserEntity) {
        for (let i = 0; i < messages.length; i++) {
            messages[i] = messages[i].copyWith({
                ...this.prepareToSend(new BodyMessage({
                    message: messages[i],
                    internalChat: currentInternalChat,
                    sender: userLogged,
                }), i)
            });
        }
        this.updateMessage(currentInternalChat, messages, true, false);
        this.handleUpdateInternalChat(currentInternalChat.copyWith({
            lastMessage: messages[messages.length - 1],
        }));
    }

    private getNextPage(scrollPosition: 'top' | 'bottom'): number {
        if (!!this.currentInternalChatMessages.pagesLoad) {
            if (scrollPosition === 'top') {
                return findMaxNumber(this.currentInternalChatMessages.pagesLoad) + 1;
            }
            return (findMinNumber(this.currentInternalChatMessages.pagesLoad) - 1) ?? 0;
        } else {
            if (scrollPosition === 'top') {
                return this.currentInternalChatMessages.page + 1;
            }
            return this.currentInternalChatMessages.page - 1;
        }
    }

    @action
    loadMoreMessages = async (scrollPosition: 'top' | 'bottom'): Promise<boolean> => {
        try {
            if (!this.loading) {
                this.setScrollPosition(scrollPosition);
                const nextPage = this.getNextPage(scrollPosition);
                const currentInternalChat = this.currentInternalChat!;
                const cacheMessageByNextPage = this.getMessagesCacheFromPage(nextPage);
                this.startLoading();
                if (cacheMessageByNextPage && cacheMessageByNextPage.length > 0) {
                    this.updateMessage(
                        currentInternalChat,
                        cacheMessageByNextPage,
                        false,
                        true,
                        this.currentInternalChatMessages.copyWith({
                            page: nextPage,
                            hasMore: nextPage < (this.currentInternalChatMessages.pages - 1)
                        }),
                    );
                    await sleep(.5);
                    return true;
                } else if (
                    (this.currentInternalChatMessages.hasMore && scrollPosition === 'top') ||
                    (this.currentInternalChatMessages.hasMoreBottom && scrollPosition === 'bottom')
                ) {
                    const response = await this._repository.loadMessages(
                        this.currentInternalChat!.id,
                        nextPage,
                    );
                    this.updateMessage(currentInternalChat, response.data, false, true, response);
                    await sleep(.5);
                    return true;
                } else {
                    this.stopLoading();
                }
            }
            return false;
        } catch (err: any) {
            this.stopLoading();
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            return false;
        }

    }

    @action
    deleteMessage = async (currenInternalChat: InternalChatEntity, message: InternalChatMessageEntity): Promise<void> => {
        try {
            message.deleted = true;
            this.updateMessage(currenInternalChat, [message], undefined, undefined,
                undefined, undefined, undefined, true);
            await this._deleteInternalChatMessageUseCase.call(message.id);
        } catch (err: any) {
            message.deleted = false;
            this.updateMessage(currenInternalChat, [message]);
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    @action
    editMessage = async (
        editText: string,
        currentMessage: InternalChatMessageEntity,
        currentInternalChat: InternalChatEntity
    ): Promise<void> => {
        try {
            if (!!editText && currentMessage.getTextEditable !== editText) {
                const editMessage = currentMessage.setEditableText(editText);
                this.updateMessage(
                    currentInternalChat, [editMessage], undefined, undefined,
                    undefined, undefined, undefined, true
                );
                await this._editMessageUseCase.call(currentMessage.id, editText);
            }
        } catch (err: any) {
            this.updateMessage(currentInternalChat, [currentMessage], undefined, undefined,
                undefined, undefined, undefined, true);
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    @action
    async retrySendMessage(
        message: InternalChatMessageEntity,
        currentInternalChat: InternalChatEntity,
        userLogged: UserEntity
    ): Promise<void> {
        const messageRetry = message.copyWith({
            status: MessageStatus.PENDING,
            statusError: null
        });
        this.updateMessage(currentInternalChat, [messageRetry]);
        await this.handleSendMessages([messageRetry], currentInternalChat, userLogged);
    }

    @action
    async handleAddReaction(
        messageToReacted: InternalChatMessageEntity,
        reactionText: string,
        userLogged: UserEntity,
        currentInternalChat: InternalChatEntity
    ): Promise<void> {
        this.setLastUsedReactions(reactionText);
        const reactionMessage: InternalChatMessageEntity | undefined = this.getReactionByUser(userLogged, messageToReacted);
        if (reactionMessage && reactionText === reactionMessage?.reactionMessage?.text) {
            await this.removeReaction(messageToReacted, reactionMessage, currentInternalChat);
        } else {
            await this.insertReaction(messageToReacted, reactionText, userLogged, currentInternalChat);
        }
    }

    @action
    removeReaction = async (
        messageReacted: InternalChatMessageEntity,
        reaction: InternalChatMessageEntity,
        currentInternalChat: InternalChatEntity,
    ): Promise<void> => {
        try {
            const reactionToRemove = reaction.copyWith({
                deleted: true,
                reactionMessage: new ReactionMessageEntity({
                    text: '',
                    messageReactedId: reaction.reactionMessage!.messageReactedId,
                    fromMe: reaction.reactionMessage!.fromMe,
                    participantId: reaction.reactionMessage?.participantId ?? '',
                })
            });
            this.updateMessage(currentInternalChat, [reactionToRemove], undefined,
                undefined, undefined, undefined, undefined, true);
            this.handleUpdateInternalChat(currentInternalChat.copyWith({
                lastMessage: this.currentInternalChatMessages.data[0],
            }));

            await this._reactionMessageUseCase.call(messageReacted.id, reactionToRemove.id, '');

        } catch (err: any) {
            this.updateMessage(currentInternalChat, [reaction], undefined, undefined,
                undefined, false, undefined, true);
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    private setLastUsedReactions = (reaction: string) => {
        const hasReaction: boolean = this.lastUsedReactions.filter((entry) => entry === reaction).length > 0;
        if (!hasReaction) {
            this.lastUsedReactions.pop();
            const lastUsedReactions = [reaction, ...this.lastUsedReactions];
            this.lastUsedReactions = lastUsedReactions;
            localStorage.setItem(ConstantsKeys.lastUsedReactions, JSON.stringify(lastUsedReactions));
        }
    }

    private insertReaction = async (
        messageReacted: InternalChatMessageEntity,
        reaction: string,
        userLogged: UserEntity,
        currentInternalChat: InternalChatEntity
    ): Promise<void> => {
        const body = new BodyMessage({
            message: newInternalChatMessage(currentInternalChat.id),
            internalChat: this.currentInternalChat!,
            sender: userLogged
        });

        const hasReaction = this.getReactionByUser(userLogged, messageReacted);
        body.message = body.message.copyWith({
            ...this.prepareToSend(body),
            id: hasReaction ? hasReaction.id : body.message.id,
            type: MessageChatType.reactionMessage,
            reactionMessage: new ReactionMessageEntity({
                text: reaction,
                messageReactedId: messageReacted.idExternal ?? messageReacted.id,
                fromMe: true,
                participantId: hasReaction && hasReaction.reactionMessage?.participantId ? hasReaction.reactionMessage?.participantId : ''
            })
        });
        try {
            this.updateMessage(body.internalChat, [body.message], undefined, undefined, undefined, undefined, undefined, true);
            this.handleUpdateInternalChat(currentInternalChat.copyWith({
                lastMessage: body.message,
            }));
            await this._reactionMessageUseCase.call(messageReacted.id, body.message.id, reaction);
        } catch (err: any) {
            this.updateMessage(body.internalChat, [body.message], undefined, undefined, undefined, true, undefined, true);
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
        }
    }

    private getReactionByUser = (userLogged: UserEntity, messageToReacted: InternalChatMessageEntity): InternalChatMessageEntity | undefined => {
        return this.reactions.find((entry) => entry.senderId === userLogged.id && (messageToReacted.idExternal === entry.reactionMessage?.messageReactedId || messageToReacted.id === entry.reactionMessage?.messageReactedId));
    }

    @action
    handleMoveCursorToFinal = () => {
        const messages = this.getMessagesCacheFromPage(0);
        this.updateMessage(
            this.currentInternalChat!,
            messages,
            false,
            true,
            this.currentInternalChatMessages.copyWith({
                page: 0,
            }),
            false,
            true,
        );
        this.handleVirtualizedMessages();
    }

    @action
    findPageById(messageId: string) {
        const data = this.cacheChat[this.currentInternalChat?.id ?? 'unknown'];
        for (const page in data) {
            if (data.hasOwnProperty(page)) {
                const objectsOnPage = data[page];
                for (const object of objectsOnPage) {
                    if (object.id === messageId || object.idExternal === messageId) {
                        return parseInt(page);
                    }
                }
            }
        }
        return null;
    }

    @action
    handleClickOnMessageReply = async (reply: ReplyEntity) => {
        try {
            const page = this.findPageById(reply.messageId);
            const currentInternalChat = this.currentInternalChat!;
            this.setTargetVirtualizedMessage(null);
            if (page !== null) {
                this.startLoading();
                const cacheMessages = this.getMessagesCacheFromPage(page);
                this.updateMessage(currentInternalChat, cacheMessages, false, true,
                    this.currentInternalChatMessages.copyWith({
                        page: page,
                    }), false, true);
                await sleep(.1);
                this.setIdMessageFocused(reply.messageId);
                this.stopLoading();
            } else {
                this.startLoading();
                this.setForceCleanMessagesCache(true);
                const response = await this._navigateToMessageUseCase.call(reply.messageId, this.currentInternalChat!.id);
                this.updateMessage(currentInternalChat, response.data, false, true, response, false, true);
                this.setForceCleanMessagesCache(false);
                await sleep(.5);
                this.setIdMessageFocused(reply.messageId);
                this.stopLoading();
            }

        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
            this.setForceCleanMessagesCache(false);
        }
    }

    @action
    prepareToSend = (body: BodyMessage, index?: number): InternalChatMessageEntity => {
        const {internalChat, sender, message} = body;
        const senderId = body.sender.id;

        const received: Record<string, boolean> = {};
        const readers: Record<string, boolean> = {};

        internalChat.users?.forEach(user => {
            const userId = user.idUser;
            received[userId] = userId === senderId;
            readers[userId] = userId === senderId;
        });

        return message.copyWith({
            id: message.id ?? generateUUIDV4(),
            idCompany: getIdCompanyByLocalStorage(),
            idInternalChat: internalChat.id,
            internalChat,
            senderId: sender.id,
            users: internalChat.getUsersForMessage(),
            sender: sender,
            received,
            readers,
            createdAtLocal: getNowSumIndex(index ?? 0),
            updatedAt: new Date().toISOString()
        });
    }

    @action
    async handleSendMessages(messages: InternalChatMessageEntity[], currentInternalChat: InternalChatEntity, userLogged: UserEntity): Promise<void> {
        this.pushMemoryMessages(messages, currentInternalChat, userLogged);
        for (let index = 0; index < messages.length; index++) {
            const body = new BodyMessage({
                message: messages[index],
                internalChat: currentInternalChat!,
                sender: userLogged
            });
            this.sendMessage(body);
        }
    };

    @action
    parseAudioToMessage = async (
        internalChat: InternalChatEntity,
        audioRecorder: AudioRecordEntity,
        sender: UserEntity,
        message: InternalChatMessageEntity
    ): Promise<InternalChatMessageEntity> => {
        const fileName = `audio${new Date().getTime()}.wav`;
        const mimeType = "audio/wav";
        const blob = await this._urlToBlobUseCase.call(audioRecorder.audioBlob!);
        const urlDownload = audioRecorder.audioBlob!;
        const file = new File([blob], fileName, {type: mimeType});
        const base64 = await fileToBase64(file);

        return new InternalChatMessageEntity({
            deleted: false,
            idCompany: getIdCompanyByLocalStorage(),
            idInternalChat: internalChat.id,
            id: generateUUIDV4(),
            sender,
            senderId: sender.id,
            type: MessageChatType.audioMessage,
            blobURL: audioRecorder.audioBlob,
            audioMessage: new AudioMessageEntity({
                buffer: undefined,
                length: file.size,
                mimetype: mimeType,
                ptt: true,
                seconds: audioRecorder.duration
            }),
            uploadOptions: new UploadOptionsEntity({
                file: new FileMessageEntity({
                    base64Url: base64,
                    id: generateUUIDV4(),
                    file,
                    description: '',
                    blobURL: urlDownload,
                    isPrivate: message.isPrivate,
                }),
                progress: 0,
            }),
            createdAtLocal: new Date().toISOString(),
            isPrivate: message.isPrivate,
            fromMe: true,
            status: MessageStatus.PENDING,
            reply: message.reply
        })
    }

    @action
    parseFileToMessage = (internalChat: InternalChatEntity, fileMessages: FileMessageEntity[], sender: UserEntity): InternalChatMessageEntity[] => {
        return fileMessages.map((fileMessage) => {
                const type: MessageChatType = getMessageTypeFromFile(fileMessage);
                const {
                    imageMessage,
                    videoMessage,
                    documentMessage
                } = convertFileToMessageEntity(type, fileMessage);


                return new InternalChatMessageEntity({
                    deleted: false,
                    idCompany: getIdCompanyByLocalStorage(),
                    idInternalChat: internalChat.id,
                    id: generateUUIDV4(),
                    sender,
                    senderId: sender.id,
                    audioMessage: null,
                    videoMessage,
                    imageMessage,
                    documentMessage,
                    blobURL: fileMessage.blobURL,
                    textMessage: null,
                    uploadOptions: new UploadOptionsEntity({
                        file: fileMessage,
                        progress: 0,
                    }),
                    type,
                    isPrivate: fileMessage.isPrivate,
                    fromMe: true,
                    status: MessageStatus.PENDING,
                    createdAtLocal: new Date().toISOString(),
                    showOriginal: true,
                })
            }
        );
    }

    private resetCountNotification(internalChatId: string): void {
        const index = this.tableListInternalChats.data.findIndex(item => internalChatId === item.id);
        if (index > -1) {
            this.tableListInternalChats.data[index] = this.tableListInternalChats.data[index].copyWith({
                countUnreadNotification: 0
            });
        }
    }

    private getUniqueCacheMessagesList(originalList: InternalChatMessageEntity[], newMessages: InternalChatMessageEntity[], isActionDelete = false): InternalChatMessageEntity[] {
        const result = originalList;

        if (newMessages.length) {
            for (const message of newMessages) {
                const index = originalList.findIndex((item) => item.id === message.id);
                if (index > -1) {
                    if (isActionDelete) {
                        result.splice(index, 1);
                    } else {
                        result[index] = result[index].getMessagePreventDefaultCache(message);
                    }
                } else {
                    result.push(message);
                }
            }
        }

        return result;
    }

    @action
    setReadByUsers = (users: UserEntity[]) => {
        this.readByUsers = users;
    }

    @action
    setReceivedByUsers = (users: UserEntity[]) => {
        this.receivedByUsers = users;
    }

    @action
    getUsersFromCache = async (message: ChatMessageEntity): Promise<void> => {
        this.receivedByUsers = [];
        this.readByUsers = [];
        if (message.readers) {
            const eligibleUsers = Object.keys(message.readers).filter(userId => message!.readers![userId] === true);
            const result = await this._getUsersFromCacheUseCase.call(eligibleUsers);
            this.setReadByUsers(result);
        }

        if (message.received) {
            const eligibleUsers = Object.keys(message.received).filter(userId => message!.received![userId] === true);
            const result = await this._getUsersFromCacheUseCase.call(eligibleUsers);
            this.setReceivedByUsers(result);
        }
    }

    @action
    handleDetailMessage = async (message: ChatMessageEntity): Promise<void> => {
        try {
            const remoteMessage = await this.getMessageById(message.id);
            await this.getUsersFromCache(remoteMessage);
        } catch (err: any) {
            toastMessage.error(translate(`server_messages.${err.message ?? 'unknown'}`));
            this.stopLoading();
        }
    }

    @action
    getMessageById = async (id: string): Promise<InternalChatMessageEntity> => {
        return new Promise<InternalChatMessageEntity>(async (resolve, reject) => {
            try {
                const result = await this._getMessageByIdUseCase.call(id);
                return resolve(result);
            } catch (err: any) {
                return reject(err);
            }
        });
    }
}
